/**
 * (owner, Sep 25: "detail/performance settings for slower mobile devices or desktops") The graphics quality: four presets of what
 * really costs on a weak device, and Auto — a level picked by the device, then stepped down while the frames run slow and back up
 * (never past the device's own pick) when they have long been easy. Settings › Graphics quality; a change to one of its parts that
 * has its own setting (the roofs) makes it Custom. The numbers behind the presets: docs/PERF.md "Graphics quality".
 */
export type QualityLevel = 'high' | 'medium' | 'low' | 'ultralow';
export const QUALITY_LEVELS: readonly QualityLevel[] = ['high', 'medium', 'low', 'ultralow'];

export interface QualitySpec {
  /** canvas pixels per CSS pixel, at most (a screen with fewer keeps its own) — the fill cost of every draw scales with its square */
  dpr: number;
  /** pitched roofs with depth (the Roofs setting) */
  roofs3d: boolean;
  /** buildings' and aircraft's shadows on the ground */
  shadows: boolean;
  /** tree crowns drawn again over whatever passes under them */
  canopies: boolean;
  /** tyre marks and dust behind the wheels */
  marks: boolean;
  /** the share of the rain and snow streaks drawn */
  weather: number;
  /** pixels a tile in the cached ground of a chunk (16: a 1024² canvas a chunk; 8: a quarter of the pixels to paint and hold) */
  groundPx: number;
  /** the zoom the wheel and the pinch go out to on the ground (further out is more chunks, buildings and cars a frame) */
  minZoom: number;
  /** the minimap's picture is redrawn every this many frames */
  miniEvery: number;
  /** vehicles drawn with their height at most this deep (0: flat, the world's own setting when lower) */
  vehicleDepth: number;
  /** (Sep 28) people's feet and swinging arms drawn live, and the shells and smoke of a shot (off: each person one stamped picture, as before) */
  limbs: boolean;
  /** (Oct 1) vehicles in full: an inset's own walls and top, steep ramps cut finely — off, one layer and at most two strips a ramp (the phones' presets: a city core drew a third slower at a phone's rate) */
  vehicleDetail: boolean;
}

export const QUALITY: Record<QualityLevel, QualitySpec> = {
  high: { dpr: 2, roofs3d: true, shadows: true, canopies: true, marks: true, weather: 1, groundPx: 16, minZoom: 0.25, miniEvery: 6, vehicleDepth: 1, limbs: true, vehicleDetail: true },
  medium: { dpr: 1.5, roofs3d: true, shadows: true, canopies: true, marks: true, weather: 0.6, groundPx: 16, minZoom: 0.35, miniEvery: 8, vehicleDepth: 1, limbs: true, vehicleDetail: false },
  low: { dpr: 1, roofs3d: false, shadows: false, canopies: true, marks: false, weather: 0.35, groundPx: 12, minZoom: 0.45, miniEvery: 12, vehicleDepth: 1, limbs: true, vehicleDetail: false },
  ultralow: { dpr: 0.75, roofs3d: false, shadows: false, canopies: false, marks: false, weather: 0.15, groundPx: 8, minZoom: 0.6, miniEvery: 20, vehicleDepth: 0, limbs: false, vehicleDetail: false },
};

export const QUALITY_NAMES: Record<QualityLevel, string> = { high: 'high', medium: 'medium', low: 'low', ultralow: 'ultra-low' };

/** The level Auto starts at: a phone or a tablet by its memory and cores (Safari tells neither: a phone there is taken as mid-range),
 *  a desktop high unless it has two cores or fewer. */
export function deviceLevel(d: { touch: boolean; cores?: number; memoryGb?: number }): QualityLevel {
  const cores = d.cores ?? 4;
  if (d.touch) {
    const mem = d.memoryGb ?? 4;
    if (mem <= 2 || cores <= 2) return 'ultralow';
    if (mem <= 3 || cores <= 4) return 'low';
    return 'medium';
  }
  if (cores <= 2 || (d.memoryGb !== undefined && d.memoryGb <= 2)) return 'low';
  if (cores <= 4 || (d.memoryGb !== undefined && d.memoryGb <= 4)) return 'medium';
  return 'high';
}

/**
 * Auto's second half: the frames' own numbers. Every drawn frame reports how long it was since the last one (`interval`) and how
 * long its drawing took — the world and the HUD, not the simulation's steps, which no picture setting makes cheaper (`draw`), in
 * ms. Every WINDOW seconds it judges: the screen's own frame time is the window's fastest intervals (a frame-rate cap is a floor
 * under it). Two windows in a row where a tenth of the frames' drawing took over 60 % of that, or a fifth of the frames missed it
 * by half again while the drawing was a third of it, step down one level. It steps back up after UP_AFTER seconds of drawing under
 * 25 % of the frame — never past the device's pick, and not at all once a step up has been undone (a level that could not hold).
 */
export class QualityGovernor {
  static readonly WINDOW = 4;
  static readonly UP_AFTER = 30;
  level: QualityLevel;
  private draws: number[] = [];
  private gaps: number[] = [];
  private t = 0;
  private easy = 0;
  private bad = 0;
  /** (the first two windows are the start: the chunks arriving, the ground painting — not judged) */
  private wait = QualityGovernor.WINDOW * 4; // (Oct 2: two windows were not the start — a desktop stepped down at 13 s while the first chunks and sprites were still coming)
  private upFailed = false;
  private lastUp = false;
  /** what it did, for the debug line: the last change and why */
  note = '';
  constructor(readonly top: QualityLevel, start: QualityLevel = top) { this.level = QUALITY_LEVELS.indexOf(start) < QUALITY_LEVELS.indexOf(top) ? top : start; }

  /** One drawn frame. `capMs`: the frame-rate cap's interval (0 = none). Returns the new level when it changes. */
  frame(interval: number, draw: number, capMs = 0): QualityLevel | undefined {
    if (!(interval > 0) || interval > 250) return undefined; // (a hidden tab, a stall of the page's own: not the game's frames)
    this.draws.push(draw); this.gaps.push(interval); this.t += interval / 1000;
    if (this.t < QualityGovernor.WINDOW) return undefined;
    const gaps = [...this.gaps].sort((a, b) => a - b), draws = [...this.draws].sort((a, b) => a - b);
    // (Oct 2: on a 120 Hz desktop the budget was 8.3 ms and the steps' misses counted against the picture: high → ultra-low in 40 s, never back)
    const n = gaps.length, frameMs = Math.max(capMs, gaps[Math.floor(n * 0.1)], 1000 / 60), span = this.t;
    const drawP90 = draws[Math.floor(n * 0.9)], missed = this.gaps.filter((g) => g > frameMs * 1.5).length / n;
    this.draws.length = 0; this.gaps.length = 0; this.t = 0;
    if (this.wait > 0) { this.wait -= span; return undefined; }
    const i = QUALITY_LEVELS.indexOf(this.level);
    const slow = drawP90 > frameMs * 0.6 || (missed > 0.2 && drawP90 > frameMs * 0.45);
    this.bad = slow ? this.bad + 1 : 0;
    if (this.bad >= 2 && i < QUALITY_LEVELS.length - 1) {
      if (this.lastUp) this.upFailed = true;
      this.lastUp = false; this.easy = 0; this.bad = 0; this.wait = QualityGovernor.WINDOW; // (a window for the new level to settle before it is judged)
      this.level = QUALITY_LEVELS[i + 1];
      this.note = `down: drawing p90 ${drawP90.toFixed(1)} ms of ${frameMs.toFixed(1)}, ${Math.round(missed * 100)} % missed`;
      return this.level;
    }
    if (drawP90 < frameMs * 0.35 && missed < 0.1) this.easy += span; else this.easy = 0;
    if (this.easy >= QualityGovernor.UP_AFTER / 2) this.lastUp = false; // (a level held a while after a step up has proved itself)
    if (this.easy >= QualityGovernor.UP_AFTER && !this.upFailed && i > QUALITY_LEVELS.indexOf(this.top)) {
      this.easy = 0; this.lastUp = true; this.wait = QualityGovernor.WINDOW;
      this.level = QUALITY_LEVELS[i - 1];
      this.note = `up: drawing p90 ${drawP90.toFixed(1)} ms of ${frameMs.toFixed(1)}`;
      return this.level;
    }
    return undefined;
  }
}
